import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  FileText, 
  Facebook, 
  Instagram, 
  Search, 
  Mail, 
  Copy, 
  Trash2,
  Filter
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatIcons = {
  facebook: Facebook,
  instagram: Instagram,
  google: Search,
  email: Mail,
  landing_page: FileText
};

const formatLabels = {
  facebook: "Facebook",
  instagram: "Instagram", 
  google: "Google Ads",
  email: "Email",
  landing_page: "Landing Page"
};

const formatColors = {
  facebook: "bg-blue-100 text-blue-700",
  instagram: "bg-pink-100 text-pink-700",
  google: "bg-green-100 text-green-700",
  email: "bg-purple-100 text-purple-700",
  landing_page: "bg-orange-100 text-orange-700"
};

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (template) => {
    const text = `${template.headline}\n\n${template.body}\n\n${template.call_to_action}${
      template.hashtags?.length ? `\n\n${template.hashtags.join(" ")}` : ""
    }`;
    
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm("Delete this template?")) return;
    
    try {
      await axios.delete(`${API}/templates/${templateId}`);
      toast.success("Template deleted");
      fetchTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Failed to delete template");
    }
  };

  const filteredTemplates = filter === "all" 
    ? templates 
    : templates.filter(t => t.ad_format === filter);

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="templates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 md:mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-['Outfit'] font-bold text-slate-900">Templates</h1>
          <p className="text-sm md:text-base text-slate-500 mt-1">Your saved ad templates</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-full sm:w-48" data-testid="template-filter-select">
            <SelectValue placeholder="Filter by format" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Formats</SelectItem>
            <SelectItem value="facebook">Facebook</SelectItem>
            <SelectItem value="instagram">Instagram</SelectItem>
            <SelectItem value="google">Google Ads</SelectItem>
            <SelectItem value="email">Email</SelectItem>
            <SelectItem value="landing_page">Landing Page</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="spinner"></div>
        </div>
      ) : filteredTemplates.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map((template) => {
            const Icon = formatIcons[template.ad_format];
            return (
              <Card 
                key={template.id} 
                className="border-slate-100 card-hover"
                data-testid={`template-card-${template.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <Badge className={`${formatColors[template.ad_format]} border-0`}>
                      <Icon className="w-3 h-3 mr-1" />
                      {formatLabels[template.ad_format]}
                    </Badge>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => copyToClipboard(template)}
                        data-testid={`copy-template-${template.id}`}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => deleteTemplate(template.id)}
                        className="text-red-500 hover:text-red-600"
                        data-testid={`delete-template-${template.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <CardTitle className="font-['Outfit'] text-lg mt-2">{template.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Headline</p>
                      <p className="font-semibold text-slate-900">{template.headline}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Body</p>
                      <p className="text-sm text-slate-700 line-clamp-3">{template.body}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">CTA</p>
                      <span className="inline-block px-4 py-2 bg-[#A94442] text-white rounded-full text-sm font-medium">
                        {template.call_to_action}
                      </span>
                    </div>
                    {template.hashtags && template.hashtags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {template.hashtags.slice(0, 3).map((tag, i) => (
                          <span key={i} className="text-xs text-slate-500">{tag}</span>
                        ))}
                        {template.hashtags.length > 3 && (
                          <span className="text-xs text-slate-400">+{template.hashtags.length - 3} more</span>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border-slate-100">
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h3 className="font-['Outfit'] font-semibold text-slate-700 mb-2">No Templates Yet</h3>
            <p className="text-slate-500 mb-4">
              Generate ads and save them as templates for quick access
            </p>
            <Button 
              onClick={() => window.location.href = "/generator"}
              className="bg-[#A94442] hover:bg-[#8B3836] rounded-full"
              data-testid="go-to-generator-btn"
            >
              Go to Ad Generator
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
